/**
 * Google Calendar sync via backend
 * Frontend calls backend endpoints which handle Google Calendar Watch API
 * Backend receives webhooks and updates Firestore
 * Frontend listens to Firestore for real-time updates
 */

import { SETUP_CALENDAR_WATCH_ENDPOINT } from '@/config/endpoints'

interface SetupWatchResponse {
  success: boolean
  watchId?: string
  resourceId?: string
  expiration?: number
  message?: string
  error?: string
}

/**
 * Request backend to set up Google Calendar watch
 * This will set up a webhook that Google will POST to
 * Backend will then fetch events and update Firestore
 * 
 * @param accessToken - Artist's Google OAuth access token
 * @param artistId - Artist's ID in the system
 * @returns Watch setup details from backend
 */
export async function setupGoogleCalendarWatch(
  accessToken: string,
  artistId: string
): Promise<SetupWatchResponse> {
  try {
    console.log('[GoogleCalendar] Calling backend to setup watch for artist:', artistId)
    console.log('[GoogleCalendar] Access token being sent to backend:', accessToken)

    const response = await fetch(SETUP_CALENDAR_WATCH_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accessToken,
        artistId,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(
        `Backend failed to setup calendar watch: ${errorData.error || response.statusText}`
      )
    }

    const data: SetupWatchResponse = await response.json()

    if (data.success) {
      console.log('[GoogleCalendar] Watch setup successful:', {
        watchId: data.watchId,
        expiration: data.expiration,
      })
    } else {
      console.error('[GoogleCalendar] Watch setup failed:', data.error)
    }

    return data
  } catch (error) {
    console.error('[GoogleCalendar] Error setting up watch:', error)
    throw error
  }
}

/**
 * Note on real-time updates:
 * 
 * After setupGoogleCalendarWatch is called:
 * 1. Backend sets up webhook with Google Calendar
 * 2. When calendar changes, Google POSTs to backend webhook
 * 3. Backend fetches events and updates Firestore: bookingSystems/{artistId}
 * 4. Frontend's Firestore listener (in BookingSystemContext) automatically
 *    receives the update and refreshes googleCalendar.events
 * 5. UI updates in real-time!
 * 
 * No polling needed on frontend - Firestore listener handles it all.
 */

